import type { ColorSpec } from './types';

/** Blue color spec — uses the `astronaut` Tailwind palette. */
export const blue: ColorSpec = {
  filled: {
    border: 'border-astronaut-700 dark:border-astronaut-500',
    bg: 'bg-astronaut-600 text-white dark:bg-astronaut-700',
    bgActive: 'bg-astronaut-800 text-white',
    hover: 'hover:bg-astronaut-700 hover:text-white active:bg-astronaut-800 active:text-white',
    hoverDark:
      'dark:hover:bg-astronaut-800 dark:hover:text-white dark:active:bg-astronaut-900 dark:active:text-white',
  },
  outline: {
    border: 'border-astronaut-400 dark:border-astronaut-500',
    bg: 'bg-astronaut-50 dark:bg-astronaut-950 text-astronaut-700 dark:text-astronaut-200',
    bgActive: 'bg-astronaut-600 dark:bg-astronaut-500 text-white',
    hover: 'hover:bg-astronaut-100 hover:text-astronaut-800 active:bg-astronaut-200 active:text-astronaut-800',
    hoverDark:
      'dark:hover:bg-astronaut-900 dark:hover:text-astronaut-100 dark:active:bg-astronaut-800 dark:active:text-astronaut-100',
    hoverWhenActive: 'hover:bg-astronaut-700 dark:hover:bg-astronaut-600',
  },
  soft: {
    border: 'border-astronaut-100 dark:border-astronaut-900',
    bg: 'bg-astronaut-50 dark:bg-astronaut-950 text-astronaut-700 dark:text-astronaut-300 font-normal',
    bgActive: 'bg-astronaut-600 dark:bg-astronaut-500 text-white',
    hover: 'hover:bg-astronaut-100 hover:text-astronaut-900 active:bg-astronaut-200 active:text-astronaut-950',
    hoverDark:
      'dark:hover:bg-astronaut-900 dark:hover:text-astronaut-100 dark:active:bg-astronaut-800 dark:active:text-astronaut-100',
    hoverWhenActive: 'hover:bg-astronaut-700 dark:hover:bg-astronaut-600',
  },
  textFilled: {
    text: 'text-astronaut-600 dark:text-astronaut-400',
    activeBg: 'bg-astronaut-600 text-white dark:bg-astronaut-700',
    hover: 'hover:bg-astronaut-600 hover:text-white active:bg-astronaut-700',
    hoverDark: 'dark:hover:bg-astronaut-700 dark:hover:text-white dark:active:bg-astronaut-800',
  },
  textSoft: {
    text: 'text-astronaut-700 dark:text-astronaut-300 font-normal',
    activeBg: 'bg-astronaut-600 text-white dark:bg-astronaut-500',
    hover: 'hover:bg-astronaut-100 hover:text-astronaut-900 active:bg-astronaut-200',
    hoverDark: 'dark:hover:bg-astronaut-900 dark:hover:text-astronaut-100 dark:active:bg-astronaut-800',
  },
  tooltip: 'bg-astronaut-600 dark:bg-astronaut-700 text-white',
  badge: {
    filled: 'bg-astronaut-600 dark:bg-astronaut-700 text-white',
    soft: 'bg-astronaut-400 dark:bg-astronaut-500 text-white',
  },
  softIcon: 'text-astronaut-700 dark:text-astronaut-200',
  underline: {
    filled:
      '[&_u]:text-astronaut-200 dark:[&_u]:text-astronaut-300 [&_u]:decoration-astronaut-300 dark:[&_u]:decoration-astronaut-400',
    outline:
      '[&_u]:text-astronaut-800 dark:[&_u]:text-astronaut-300 [&_u]:decoration-astronaut-600 dark:[&_u]:decoration-astronaut-400',
    soft: '[&_u]:text-astronaut-800 dark:[&_u]:text-astronaut-300 [&_u]:decoration-astronaut-300 dark:[&_u]:decoration-astronaut-400',
  },
  input: {
    filled: {
      border: 'border-astronaut-700 dark:border-astronaut-500',
      bg: 'bg-astronaut-600 dark:bg-astronaut-700',
      focusBg: 'focus-within:bg-astronaut-700 dark:focus-within:bg-astronaut-800',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-astronaut-700 dark:focus-within:outline-astronaut-500',
      hoverActive:
        'hover:bg-astronaut-700 hover:text-white active:bg-astronaut-800 active:text-white dark:hover:bg-astronaut-800 dark:active:bg-astronaut-900',
      readOnlyBg: 'bg-astronaut-500 dark:bg-astronaut-800',
      text: 'text-white',
    },
    outline: {
      border: 'border-astronaut-400 dark:border-astronaut-500',
      bg: 'bg-astronaut-50 dark:bg-astronaut-950',
      focusBg: 'focus-within:bg-astronaut-100 dark:focus-within:bg-astronaut-900',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-astronaut-400 dark:focus-within:outline-astronaut-500',
      hoverActive:
        'hover:bg-astronaut-100 hover:text-astronaut-800 active:bg-astronaut-200 active:text-astronaut-800 dark:hover:bg-astronaut-900 dark:hover:text-astronaut-100 dark:active:bg-astronaut-800 dark:active:text-astronaut-100',
      readOnlyBg: 'bg-astronaut-50 dark:bg-astronaut-900',
      text: 'text-astronaut-700 dark:text-astronaut-200',
    },
    soft: {
      border: 'border-astronaut-100 dark:border-astronaut-900',
      bg: 'bg-astronaut-50 dark:bg-astronaut-950',
      focusBg: 'focus-within:bg-astronaut-100 dark:focus-within:bg-astronaut-900',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-astronaut-100 dark:focus-within:outline-astronaut-900',
      hoverActive:
        'hover:bg-astronaut-100 hover:text-astronaut-900 active:bg-astronaut-200 active:text-astronaut-950 dark:hover:bg-astronaut-900 dark:hover:text-astronaut-100 dark:active:bg-astronaut-800 dark:active:text-astronaut-100',
      readOnlyBg: 'bg-astronaut-50 dark:bg-astronaut-900',
      text: 'text-astronaut-700 dark:text-astronaut-300',
    },
  },
  inputMuted: {
    filled: 'text-white',
    outline: 'text-astronaut-300 dark:text-astronaut-600',
    soft: 'text-astronaut-300 dark:text-astronaut-600',
  },
  inputPlaceholder: {
    filled: 'placeholder:text-white',
    outline: 'placeholder:text-astronaut-300 dark:placeholder:text-astronaut-600',
    soft: 'placeholder:text-astronaut-300 dark:placeholder:text-astronaut-600',
  },
  group: {
    filled: 'border-astronaut-300 dark:border-astronaut-700 bg-astronaut-50 dark:bg-astronaut-950',
    outline: 'border-astronaut-400 dark:border-astronaut-500 bg-astronaut-50 dark:bg-astronaut-950',
    soft: 'border-astronaut-200 dark:border-astronaut-800 bg-astronaut-100 dark:bg-astronaut-900',
  },
  groupDivider: {
    filled: 'bg-astronaut-300 dark:bg-astronaut-700',
    outline: 'bg-astronaut-300 dark:bg-astronaut-600',
    soft: 'bg-astronaut-200 dark:bg-astronaut-700',
  },
  segment: {
    filled: 'border-astronaut-300 dark:border-astronaut-700 bg-astronaut-50 dark:bg-astronaut-950',
    outline: 'border-astronaut-400 dark:border-astronaut-500 bg-astronaut-50 dark:bg-astronaut-950',
  },
  segmentDivider: {
    filled: 'bg-astronaut-300 dark:bg-astronaut-700',
    outline: 'bg-astronaut-300 dark:bg-astronaut-600',
  },
  popoverHighlight: 'bg-astronaut-600 text-white',
  popoverText: 'text-astronaut-700 dark:text-astronaut-200',
  popoverBorder: 'border-astronaut-200 dark:border-astronaut-700',
};
